/**
 * The path prefix this SPA is mounted under, derived at RUNTIME.
 *
 * Studio is deployed behind two different front doors and the browser-facing
 * mount is a property of the deployment, not of the router:
 *
 *   - **gateway** — `matrix_gateway` is the front door. It routes by registered
 *     prefix (`/studio`, `/admin`, `/fleet`, …) and, because
 *     `MatrixStudio.GatewayRoutes` sets `strip_all`, strips `/studio` before
 *     proxying. Every URL the browser requests must therefore CARRY `/studio`;
 *     anything else is answered with `{"error":"no_route","path":"..."}`.
 *   - **direct** — `MatrixStudio.Endpoint` is reachable at `/` (the Vite dev
 *     server on :5173, Phoenix on :4000, and the `*.strike48.test` wildcard
 *     zone, which forwards straight to port 4000 with no gateway in the path).
 *     The bare path is the correct URL here. A prefixed one also routes on the
 *     Phoenix front doors, because `strip_mount_prefix` (endpoint.ex:246) runs
 *     unconditionally rather than only on proxied requests — but not through
 *     the Vite dev proxy, which is keyed on the router path. `''` is the only
 *     answer that is right on all three.
 *
 * This lives in `packages/ui` rather than in Studio's `utils/apollo.ts`
 * because `packages/ui` cannot import from the app that consumes it, and the
 * shared chat components build Studio-router URLs of their own — see
 * `utils/artifactUrl.ts`. Studio's `utils/apollo.ts` re-exports both functions,
 * so there is still exactly one implementation.
 */

/**
 * True when this SPA document was served THROUGH the gateway front-door.
 *
 * The gateway stamps `x-matrix-gateway-shell: 1` on every proxied request;
 * `MatrixStudio.Controllers.StaticHtmlHelper` mirrors it into
 * `<meta name="gateway-shell" content="1">` in the served HTML (GW-803), and
 * the gateway may alternatively inject `window.__GATEWAY_SHELL__ = true`.
 * Either signal counts. Direct access — production ingress straight to Studio,
 * the Vite dev server, direct Phoenix (:4000) — carries neither.
 *
 * Timing note: this is safe to call at module load. The metas are injected
 * immediately after `<head>` opens and Vite emits the bundle as a deferred
 * `<script type="module">`, so the DOM (including the meta) is fully parsed
 * before any module code runs.
 */
export function isGatewayServed(): boolean {
  if (
    typeof window !== 'undefined' &&
    (window as { __GATEWAY_SHELL__?: boolean }).__GATEWAY_SHELL__ === true
  ) {
    return true;
  }
  if (typeof document === 'undefined') return false;
  const content = document
    .querySelector('meta[name="gateway-shell"]')
    ?.getAttribute('content')
    ?.trim()
    .toLowerCase();
  return content === '1' || content === 'true';
}

/**
 * Derives the app prefix for gateway routing (e.g. `/studio`), or `''`.
 *
 * The gateway flag gates the whole thing, and the path segment only chooses
 * WHICH prefix. That order matters: a non-gateway deployment that happens to
 * serve the SPA at `/studio` must still yield `''`, otherwise every URL built
 * on it gains a prefix no one is there to strip.
 */
export function getAppPrefix(): string {
  if (typeof window === 'undefined') return '';

  if (!isGatewayServed()) return '';

  const match = window.location.pathname.match(/^\/(studio|chat|asoc)(?:\/|$)/);
  return match ? `/${match[1]}` : '';
}
